import React, { useCallback, useState } from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiButton,
  EuiButtonGroup,
  EuiMarkdownEditor,
} from "@elastic/eui";
import { CommentQuestion, CommentTypes } from "anketa-core/command_question";
import { useAnketa } from "../../context_provider";
import { VFileMessage } from "vfile-message";
import type {
  EuiMarkdownAstNode,
  EuiMarkdownParseError,
} from "@elastic/eui/lib/components/markdown_editor/markdown_types";
import { useAnketaAuth } from "../../../../auth/context";
import { useAnketaI18n } from "src/internationalization";

interface DropHandler {
  supportedFiles: string[];
  accepts: (itemType: string) => boolean;
  getFormattingForItem: (
    item: File
  ) => Promise<{ text: string; config: { block: boolean } }>;
}

export const CommentInput = ({
  props,
  setValue,
}: {
  props: CommentQuestion;
  setValue: (comment: string) => void;
}) => {
  const ctx = useAnketa();
  const { translate } = useAnketaI18n();
  const { user } = useAnketaAuth();
  const [messages, setMessages] = useState<string[]>([]);
  const { commentType } = props.commentValues;

  const placeholder = props.placeholder
    ? ctx.i18nContext.render(props.placeholder)
    : translate("anketa.questionnaire.editor.comment.placeholder", {
        defaultMessage: "Here you have a section for notes and questions",
      });

  const [_value, _setValue] = useState<string>("");

  const handleSubmit = () => {
    if (!props.readonly && placeholder.trim() && user.email) {
      props.updateUser(user.email);
      props.updateCreateTime();
      // onSubmit(value, commentType);
      setValue(_value);
      _setValue("");
    }
  };

  const onParse = useCallback(
    (
      error: EuiMarkdownParseError,
      data: { messages: VFileMessage[]; ast: EuiMarkdownAstNode }
    ) => {
      if (error) {
        setMessages([error.message]);
      } else {
        setMessages(data.messages.map((msg) => msg.message));
      }
    },
    []
  );

  const dropHandlers: DropHandler[] = [
    {
      supportedFiles: [".jpg", ".jpeg", ".png", ".svg"],
      accepts: (itemType: string) =>
        itemType === "image/jpeg" ||
        itemType === "image/png" ||
        itemType === "image/svg+xml",
      getFormattingForItem: (item: File) => {
        return new Promise((resolve) => {
          setTimeout(() => {
            const url = URL.createObjectURL(item);
            resolve({
              text: `![${item.name}](${url})`,
              config: { block: true },
            });
          }, 1000);
        });
      },
    },
  ];

  return (
    <EuiFlexGroup direction="column" gutterSize="m">
      <EuiFlexItem>
        <EuiMarkdownEditor
          contentEditable={!props.readonly}
          initialViewMode="editing"
          aria-label={translate(
            "anketa.questionnaire.editor.comment.editorAriaLabel",
            { defaultMessage: "Comment editor" },
          )}
          placeholder={placeholder}
          value={_value}
          onChange={_setValue}
          height={400}
          onParse={onParse}
          errors={messages}
          dropHandlers={dropHandlers}
        />
      </EuiFlexItem>
      <EuiFlexGroup direction="row" justifyContent="spaceBetween">
        <EuiFlexItem grow={false}>
          <EuiButtonGroup
            idSelected={commentType}
            type="single"
            legend={translate("anketa.questionnaire.editor.comment.type.legend", {
              defaultMessage: "Comment type",
            })}
            onChange={(id: CommentTypes) => {
              props.updateCommentType(id);
            }}
            options={[
              {
                id: "question",
                label: translate(
                  "anketa.questionnaire.editor.comment.type.question",
                  { defaultMessage: "Question" },
                ),
              },
              {
                id: "note",
                label: translate(
                  "anketa.questionnaire.editor.comment.type.note",
                  { defaultMessage: "Note" },
                ),
              },
            ]}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={handleSubmit}
            fill
            disabled={props.readonly || !placeholder.trim()}
            iconType="check"
          >
            {translate("anketa.form.lead.submit", { defaultMessage: "Submit" })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiFlexGroup>
  );
};
